import assetUtils, { Asset } from 'app-lib/assetUtils'
import IBorrowedFundsState, { LoanReturnData } from 'bzx-common/src/domain/IBorrowedFundsState'
import * as mobx from 'mobx'
import { BigNumber } from '@0x/utils'
import { isChainSupported } from '../../config/appConfig'
import RootStore from '../RootStore'

type LoanProps =
  | 'accountAddress'
  | 'loanId'
  | 'loanAsset'
  | 'collateralAsset'
  | 'amount'
  | 'amountOwed'
  | 'collateralAmount'
  | 'interestRate'
  | 'interestOwedPerDay'
  | 'collateralizedPercent'
  | 'hasManagementContract'
  | 'isInProgress'
  | 'loanData'
  | 'activeTokenLiquidation'
  | 'pendingTx'

export default class Loan {
  accountAddress = ''
  loanId = ''
  loanAsset: Asset = Asset.IETH
  collateralAsset: Asset = Asset.IETH
  amount = new BigNumber(0)
  amountOwed = new BigNumber(0)
  collateralAmount = new BigNumber(0)
  interestRate = new BigNumber(0)
  interestOwedPerDay = new BigNumber(0)
  collateralizedPercent = new BigNumber(0)
  hasManagementContract = false
  isInProgress = false
  loanData: LoanReturnData
  pendingTx = false
  activeTokenLiquidation: Asset | null = null
  liquidationPrice = new BigNumber(0)
  sliderMax = 3000
  rootStore: RootStore

  get startMargin() {
    return this.loanData.startMargin.div(10 ** 18).toNumber()
  }

  get sliderMin() {
    return this.loanData.maintenanceMargin.div(10 ** 18).toNumber()
  }

  get isUnhealthyLoan() {
    return this.collateralizedPercent.times(100).lt(this.sliderMin)
  }

  get sliderValue() {
    const sliderValue = this.collateralizedPercent.times(100).toNumber()

    if (sliderValue > this.sliderMax) {
      return this.sliderMax
    }
    if (sliderValue < this.sliderMin) {
      return this.sliderMin
    }

    return sliderValue
  }

  get selectedLiquidationPrice() {
    return this.activeTokenLiquidation === this.collateralAsset
      ? this.liquidationPrice
      : new BigNumber(1).div(this.liquidationPrice)
  }

  get unwrappedCollateralAsset() {
    return this.collateralAsset === Asset.WETH
      ? Asset.ETH
      : this.collateralAsset === Asset.WBNB
      ? Asset.BNB
      : this.collateralAsset === Asset.WMATIC
      ? Asset.MATIC
      : this.collateralAsset
  }

  updateLiquidationPrice() {
    const { chainId } = this.rootStore.web3Connection
    if (!chainId) {
      throw new Error('There is no network connection')
    }
    const loanAssetDecimals = assetUtils.getDecimals(chainId, this.loanAsset)
    const collateralAssetDecimals = assetUtils.getDecimals(chainId, this.collateralAsset)
    const loanAssetPrecision = new BigNumber(10 ** (18 - loanAssetDecimals))
    const collateralAssetPrecision = new BigNumber(10 ** (18 - collateralAssetDecimals))
    const liquidationCollateralToLoanRate = this.loanData.maintenanceMargin
      .times(this.loanData.principal.times(loanAssetPrecision))
      .div(10 ** 20)
      .plus(this.loanData.principal.times(loanAssetPrecision))
      .div(this.loanData.collateral.times(collateralAssetPrecision))
      .times(10 ** 18)

    this.liquidationPrice = liquidationCollateralToLoanRate.div(10 ** 18)
  }

  setActive(prop: any, value: any) {
    const { borrowStore } = this.rootStore
    borrowStore.set('loanId', this.loanId)
    borrowStore.set(prop, value)
  }

  /**
   * Helper to set values through mobx actions.
   */
  set(prop: LoanProps, value: any) {
    ;(this[prop] as any) = value
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  assign(props: { [key: string]: any }) {
    Object.assign(this, props)
  }

  init() {
    const { chainId } = this.rootStore.web3Connection
    this.updateLiquidationPrice()
    if (chainId && isChainSupported(chainId)) {
      this.set(
        'activeTokenLiquidation',
        assetUtils.getDefaultActiveToken(chainId, this.loanAsset, this.collateralAsset)
      )
    }

    mobx.reaction(
      () => this.pendingTx,
      () => {
        const loan = this.rootStore.borrowStore.loans.find((item) => this.loanId === item.loanId)
        loan && this.assign(loan)
      }
    )
  }

  constructor(rootStore: RootStore, loan: IBorrowedFundsState) {
    this.rootStore = rootStore
    this.assign(loan)
    this.loanData = loan.loanData
    this.init()
    mobx.makeAutoObservable(this, undefined, { deep: false, autoBind: true })
  }
}
